import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { User, Mail, Phone, Lock, Copy, CheckCircle, Sparkles, ArrowRight } from 'lucide-react';
import axiosClient from '../api/axiosClient';
import "../styles/Signup.css";
import Swal from "sweetalert2";

export default function Signup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generatedUserId, setGeneratedUserId] = useState('');

  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    fullName: '',
    lastName: '',
    email: '',
    mobileNo: '',
    userType: 'STUDENT',
    studentOrProfessionalDetails: '',
    studentOrProfessionalAim: '',
    password: '',
    confirmPassword: ''
  });

  // Returns an error message for a field ('' if valid)
  const REQUIRED_MESSAGES = {
    fullName: "First name is required",
    email: "Email is required",
    mobileNo: "Contact number is required",
    password: "Password is required",
    confirmPassword: "Please confirm your password",
  };

  const validateField = (name, value, data = formData) => {
    if (REQUIRED_MESSAGES[name] && value.trim() === "") return REQUIRED_MESSAGES[name];

    switch (name) {
      case "fullName":
      case "lastName":
        if (!/^[A-Za-z\s]*$/.test(value)) return "Numbers or special characters not allowed";
        if (value.length > 50) return "Maximum 50 characters allowed";
        return "";

      case "email":
        if (value.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return "Invalid email format";
        return "";

      case "mobileNo":
        if (value.length > 0 && value.length < 10) return "Must be exactly 10 digits";
        return "";

      case "password":
        if (value.length > 0 && value.length < 8) return "Minimum 8 characters required";
        return "";

      case "confirmPassword":
        if (value.length > 0 && value !== data.password) return "Passwords do not match!";
        return "";

      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Silently filter invalid input for mobile (not an error message, just a filter)
    if (name === "mobileNo") {
      if (!/^[0-9]*$/.test(value) || value.length > 10) return;
    }

    const updated = { ...formData, [name]: value };
    setFormData(updated);

    // Errors only appear after clicking submit.
    // After that, an existing error updates/clears live as the user fixes it.
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: validateField(name, value, updated) }));
    }

    // If password changes, re-check confirmPassword only if it already shows an error
    if (name === "password" && errors.confirmPassword) {
      setErrors(prev => ({
        ...prev,
        confirmPassword: validateField("confirmPassword", updated.confirmPassword, updated),
      }));
    }
  };

  const goToLoginOrCourses = () => {
    const fromPopup = localStorage.getItem("fromPopup");

    if (fromPopup) {
      localStorage.removeItem("fromPopup");
      navigate("/courses");
    } else {
      navigate("/login");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate every field only when the user clicks submit
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const msg = validateField(key, formData[key], formData);
      if (msg) newErrors[key] = msg;
    });

    if (formData.mobileNo.length !== 10) {
      newErrors.mobileNo = "Complete 10 digits required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please correct the errors first");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosClient.post('/no-auth/api/users/register', formData);
      const newUserId = response.data.data || response.data.userId || "ID-NOT-FOUND";
      setGeneratedUserId(newUserId);
      toast.success("Welcome to the Elite Club!");
    } catch (error) {
      const responseData = error.response?.data;

      const errorMsg =
        responseData?.message ||
        responseData?.error ||
        "Registration Failed.";

      // Account already exists
      if (
        errorMsg.toLowerCase().includes("already") ||
        errorMsg.toLowerCase().includes("exist")
      ) {
        Swal.fire({
          icon: "warning",
          title: "Account Already Exists",
          text: "This email is already registered.",
          confirmButtonColor: "#6366f1",
          color: "#111827",
        });
      }

      // Password length popup
      else if (
        responseData?.password ||
        responseData?.confirmPassword
      ) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Password",
          text:
            responseData.password ||
            responseData.confirmPassword,
          confirmButtonColor: "#6366f1",
          color: "#111827",
        });
      }

      // Other errors
      else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const ErrorMsg = ({ name }) => (
    errors[name] ? <span className="sp-error-text">{errors[name]}</span> : null
  );

  return (
    <div className="sp-wrapper">
      <div className="sp-orb sp-orb-1"></div>
      <div className="sp-orb sp-orb-2"></div>

      <div className="sp-glass-card-signup">
        {!generatedUserId ? (
          <div className="sp-signup-content">
            <div className="sp-signup-header">
              <div className="sp-badge"><Sparkles size={14} /> Join the Intelligence</div>
              <h2>Create Account</h2>
              <p>Experience the future of data-driven learning</p>
            </div>

            <form onSubmit={handleSubmit} className="sp-glamour-form" noValidate>
              <div className="sp-form-grid">
                <div className="sp-input-box">
                  <label><User size={16} /> First Name</label>
                  <input
                    type="text"
                    name="fullName"
                    required
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Full Name"
                  />
                  <ErrorMsg name="fullName" />
                </div>

                <div className="sp-input-box">
                  <label>Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Last Name"
                  />
                  <ErrorMsg name="lastName" />
                </div>

                <div className="sp-input-box">
                  <label><Mail size={16} /> Email Address</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="name@example.com"
                  />
                  <ErrorMsg name="email" />
                </div>

                <div className="sp-input-box">
                  <label><Phone size={16} /> Contact No.</label>
                  <input
                    type="text"
                    name="mobileNo"
                    required
                    value={formData.mobileNo}
                    onChange={handleChange}
                    placeholder="10-digit number"
                    maxLength="10"
                    inputMode="numeric"
                  />
                  <ErrorMsg name="mobileNo" />
                </div>

                <div className="sp-input-box">
                  <label><Lock size={16} /> Password</label>
                  <input
                    type="password"
                    name="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Password"
                  />
                  <ErrorMsg name="password" />
                </div>

                <div className="sp-input-box">
                  <label>Confirm Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm Password"
                  />
                  <ErrorMsg name="confirmPassword" />
                </div>
              </div>

              <button type="submit" className="sp-submit-glamour-btn" disabled={loading}>
                {loading ? "Authenticating..." : "Begin Journey"} <ArrowRight size={18} />
              </button>
            </form>

            <p className="sp-login-redirect">
              Already have an account? <span onClick={goToLoginOrCourses}>Sign In</span>
            </p>
          </div>
        ) : (
          <div className="sp-success-card-inner">
            <div className="sp-success-icon-wrap"><CheckCircle size={60} /></div>
            <h3>Registration Complete!</h3>
            <p>Your unique identifier is ready. Keep it safe.</p>
            <div className="sp-id-container">
              <span className="sp-id-label">YOUR USER ID</span>
              <div className="sp-id-row">
                <code>{generatedUserId}</code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(generatedUserId);
                    toast.success("Copied!");
                  }}
                  className="sp-copy-btn"
                >
                  <Copy size={18} />
                </button>
              </div>
            </div>
            <button className="sp-continue-btn" onClick={goToLoginOrCourses}>
              Proceed to Login <ArrowRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}